import React, { useState, useEffect, useMemo } from 'react'
import { Search, MoreVertical, Trash2, CheckCircle, Filter, ChevronDown, X, Bell, Package, CreditCard, MessageCircle, AlertCircle, FileText, Star } from 'lucide-react'
import { SidebarLayout } from '../../components/Sidebar'
import { HellocianSidebarLayout } from '../../components/HellocianSidebar'
import { AdminSidebarLayout } from '../../components/AdminSidebar'
import { useAuthStore } from '../../store/authStore'
import { useGetNotifications, useMarkAsRead, useMarkAllAsRead, useDeleteNotification } from '../../services/notification.service'
import SocketService from '../../services/socket.service'
import { useQueryClient } from '@tanstack/react-query'
import { formatDistanceToNow } from 'date-fns'

const NotificationsPage = () => {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [sortBy, setSortBy] = useState('newest')
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [activeDropdown, setActiveDropdown] = useState(null)
  const [selectedNotifications, setSelectedNotifications] = useState([])

  // Fetch notifications using the new service
  const { data, isLoading, isError } = useGetNotifications()
  const notifications = data?.notifications || []

  // Mutations
  const markAsReadMutation = useMarkAsRead()
  const markAllAsReadMutation = useMarkAllAsRead()
  const deleteMutation = useDeleteNotification()

  // Socket listeners for real-time updates
  useEffect(() => {
    const socket = SocketService.connect()
    if (socket) {
      SocketService.onNotification((newNotif) => {
        queryClient.setQueryData(['notifications', {}], (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            notifications: [newNotif, ...prev.notifications],
            unread: prev.unread + 1
          };
        });
      });

      SocketService.onNotificationRead(({ notificationId }) => {
        queryClient.invalidateQueries(['notifications']);
      });

      SocketService.onAllNotificationsRead(() => {
        queryClient.invalidateQueries(['notifications']);
      });
    }

    return () => {
      SocketService.offNotification();
    };
  }, [queryClient]);

  // Filter and sort notifications
  const filteredNotifications = useMemo(() => {
    return notifications
      .filter(notification => {
        const matchesSearch = notification.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             notification.message.toLowerCase().includes(searchTerm.toLowerCase())
        
        const matchesFilter = filterType === 'all' || 
                             (filterType === 'unread' && !notification.isRead) ||
                             (filterType === 'read' && notification.isRead) ||
                             notification.type === filterType ||
                             (filterType === 'order' && notification.type.startsWith('order_')) ||
                             (filterType === 'payment' && notification.type === 'payment_received') ||
                             (filterType === 'message' && notification.type === 'message_received')
        
        return matchesSearch && matchesFilter
      })
      .sort((a, b) => {
        if (sortBy === 'newest') {
          return new Date(b.createdAt) - new Date(a.createdAt)
        } else if (sortBy === 'oldest') {
          return new Date(a.createdAt) - new Date(b.createdAt)
        } else if (sortBy === 'unread') {
          return a.isRead - b.isRead
        }
        return 0
      })
  }, [notifications, searchTerm, filterType, sortBy]);

  // Handle select all
  const handleSelectAll = () => {
    if (selectedNotifications.length === filteredNotifications.length) {
      setSelectedNotifications([])
    } else {
      setSelectedNotifications(filteredNotifications.map(n => n._id))
    }
  }

  // Handle individual selection
  const handleSelectNotification = (id) => {
    setSelectedNotifications(prev => 
      prev.includes(id) 
        ? prev.filter(nId => nId !== id)
        : [...prev, id]
    )
  }

  // Mark as read
  const handleMarkAsRead = async (id) => {
    await markAsReadMutation.mutateAsync(id)
    setActiveDropdown(null)
  }

  // Delete notification
  const handleDelete = async (id) => {
    await deleteMutation.mutateAsync(id)
    setSelectedNotifications(prev => prev.filter(nId => nId !== id))
    setActiveDropdown(null)
  }

  // Bulk delete
  const handleBulkDelete = async () => {
    for (const id of selectedNotifications) {
       await deleteMutation.mutateAsync(id)
    }
    setSelectedNotifications([])
  }

  // Bulk mark as read
  const handleBulkMarkAsRead = async () => {
    if (selectedNotifications.length === filteredNotifications.length && filterType === 'all') {
        await markAllAsReadMutation.mutateAsync();
    } else {
        for (const id of selectedNotifications) {
            await markAsReadMutation.mutateAsync(id)
        }
    }
    setSelectedNotifications([])
  }

  // Get type icon and color
  const getNotificationStyles = (type) => {
    switch (type) {
        case 'order_created':
        case 'order_updated':
        case 'order_completed':
            return { icon: <Package className="w-5 h-5" />, color: 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400' };
        case 'order_cancelled':
            return { icon: <X className="w-5 h-5" />, color: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400' };
        case 'payment_received':
            return { icon: <CreditCard className="w-5 h-5" />, color: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' };
        case 'message_received':
            return { icon: <MessageCircle className="w-5 h-5" />, color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400' };
        case 'review_received':
            return { icon: <Star className="w-5 h-5" />, color: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400' };
        case 'account_blocked':
        case 'account_unblocked':
            return { icon: <AlertCircle className="w-5 h-5" />, color: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400' };
        default:
            return { icon: <Bell className="w-5 h-5" />, color: 'bg-gray-100 dark:bg-[#1d2026] text-gray-800 dark:text-gray-300' };
    }
  }

  const SidebarWrapper = user?.role === 'hellocian' ? HellocianSidebarLayout : 
                        (user?.role === 'admin' || user?.role === 'super-admin') ? AdminSidebarLayout : 
                        SidebarLayout;

  return (
    <SidebarWrapper currentPath="/notifications">
    <div className="min-h-screen bg-gray-50 dark:bg-[#0A0A0A] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl md:text-3xl font-medium md:font-bold text-[#1B2559] dark:text-white mmb-1 md:mb-2">Notifications</h1>
          <p className="text-gray-600 dark:text-gray-400 text-[12px] md:text-[15px]">Manage all your notifications in one place</p>
        </div>

        {/* Controls */}
        <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800 p-3 md:p-6 mb-6">
          {/* Search and Filter Row */}
          <div className="flex flex-col lg:flex-row gap-4 mb-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search notifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-[12px] md:text-[15px] border border-gray-300 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div className="flex items-center space-x-2">
            {/* Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-[#32353c] dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500"
              >
                <Filter className="w-4 h-4" />
                <span className="capitalize text-[12px] md:text-[15px]">{filterType === 'all' ? 'All Types' : filterType}</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1d2026] rounded-lg shadow-lg border border-gray-200 dark:border-[#32353c] z-10">
                  {['all', 'unread', 'read', 'payment', 'message', 'reminder', 'report', 'security'].map(type => (
                    <button
                      key={type}
                      onClick={() => {
                        setFilterType(type)
                        setShowFilterDropdown(false)
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 first:rounded-t-lg last:rounded-b-lg capitalize"
                    >
                      {type === 'all' ? 'All Types' : type}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Sort Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-[#32353c] dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-blue-500"
              >
                <span className="capitalize text-[12px] md:text-[15px]">
                  {sortBy === 'newest' ? 'Newest First' : 
                   sortBy === 'oldest' ? 'Oldest First' : 'Unread First'}
                </span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {showSortDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1d2026] rounded-lg shadow-lg border border-gray-200 dark:border-[#32353c] z-10">
                  {[
                    { value: 'newest', label: 'Newest First' },
                    { value: 'oldest', label: 'Oldest First' },
                    { value: 'unread', label: 'Unread First' }
                  ].map(option => (
                    <button
                      key={option.value}
                      onClick={() => {
                        setSortBy(option.value)
                        setShowSortDropdown(false)
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 first:rounded-t-lg last:rounded-b-lg"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            </div>

          </div>

          {/* Bulk Actions */}
          <div className="flex md:flex-row flex-col items-start justify-between">
            <div className="flex items-start space-x-4">
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={selectedNotifications.length === filteredNotifications.length && filteredNotifications.length > 0}
                  onChange={handleSelectAll}
                  className="w-3 h-3 md:w-4 md:h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-[10px] md:text-sm text-gray-700 dark:text-gray-300">
                  Select All ({selectedNotifications.length} selected)
                </span>
              </label>
            </div>

            {selectedNotifications.length > 0 && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={handleBulkMarkAsRead}
                  className="flex items-center space-x-1 px-3 py-1.5 text-[10px] md:text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                >
                  <CheckCircle className="md:w-4 md:h-4 h-3 w-3" />
                  <span>Mark as Read</span>
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center space-x-1 px-3 py-1.5 text-[10px] md:text-sm bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                >
                  <Trash2 className="md:w-4 md:h-4 h-3 w-3" />
                  <span>Delete</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white dark:bg-[#141414] rounded-lg shadow-sm border border-gray-200 dark:border-gray-800">
          {isLoading ? (
            <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-4 text-gray-600 dark:text-gray-400">Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="p-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-[#1d2026] rounded-full flex items-center justify-center">
                <Search className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No notifications found</h3>
              <p className="text-gray-600 dark:text-gray-400">Try adjusting your search or filter criteria</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {filteredNotifications.map((notification) => {
                const styles = getNotificationStyles(notification.type);
                return (
                  <div
                    key={notification._id}
                    className={`p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${
                      !notification.isRead ? 'bg-blue-50/30 dark:bg-blue-900/10' : ''
                    }`}
                  >
                    <div className="flex items-start space-x-4">
                      {/* Checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedNotifications.includes(notification._id)}
                        onChange={() => handleSelectNotification(notification._id)}
                        className="mt-1 md:w-4 w-3 md:h-4 h-3 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />

                      {/* Icon Container */}
                      <div className={`md:w-12 w-7 h-7 md:h-12 rounded-full flex items-center justify-center flex-shrink-0 ${styles.color}`}>
                         {styles.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-1">
                              <h3 className={`md:text-sm text-[12px] font-medium ${
                                !notification.isRead ? 'text-[#1B2559] dark:text-white' : 'text-gray-700 dark:text-gray-400'
                              }`}>
                                {notification.title}
                              </h3>
                              {!notification.isRead && (
                                <div className="md:w-2 w-1 md:h-2 h-1 bg-blue-500 rounded-full"></div>
                              )}
                            </div>
                            <div className="mb-2">
                               <p className="text-[11px] md:text-sm text-gray-600 dark:text-gray-300">{notification.message}</p>
                            </div>
                            <div className="flex items-center space-x-3 mb-2">
                              <p className="text-[9px] text-gray-500 dark:text-gray-400">
                                {notification.createdAt && formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                              </p>
                              <span className={`inline-block px-2 py-0.5 text-[10px] font-normal md:font-medium uppercase rounded-full ${styles.color}`}>
                                {notification.type.replace('_', ' ')}
                              </span>
                            </div>
                          </div>

                          {/* Actions Dropdown */}
                          <div className="relative">
                            <button
                              onClick={() => setActiveDropdown(activeDropdown === notification._id ? null : notification._id)}
                              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                            >
                              <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                            </button>

                            {activeDropdown === notification._id && (
                              <>
                                <div 
                                  className="fixed inset-0 z-10"
                                  onClick={() => setActiveDropdown(null)}
                                ></div>
                                <div className="absolute right-0 mt-1 w-48 bg-white dark:bg-[#1d2026] rounded-lg shadow-lg border border-gray-200 dark:border-[#32353c] z-20">
                                  {!notification.isRead && (
                                    <button
                                      onClick={() => handleMarkAsRead(notification._id)}
                                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-gray-300 first:rounded-t-lg"
                                    >
                                      Mark as Read
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleDelete(notification._id)}
                                    className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 last:rounded-b-lg"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Stats Footer */}
        <div className="mt-6 text-center md:text-sm text-[12px] text-gray-500 dark:text-gray-400">
          Showing {filteredNotifications.length} of {notifications.length} notifications
        </div>
      </div>
    </div>
    </SidebarWrapper>
  )
}

export default NotificationsPage